import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Receipt,
  Download,
  Visibility,
  Print,
  Email,
  Search,
  CheckCircle,
  Pending,
  AttachMoney,
  CreditCard,
  MoreVert,
} from '@mui/icons-material';

const invoiceData = [
  {
    id: 'INV-2026',
    bookingId: 'BK2026',
    date: '2025-11-10',
    dueDate: '2025-11-20',
    description: 'Deluxe Room - 3 Nights',
    amount: 540,
    tax: 54,
    total: 594,
    status: 'Paid',
    paymentMethod: 'Credit Card',
    paymentDate: '2025-11-10',
    items: [
      { description: 'Deluxe Room (3 nights @ $180/night)', amount: 540 },
      { description: 'Tax (10%)', amount: 54 },
    ],
  },
  {
    id: 'INV-2025',
    bookingId: 'BK2025',
    date: '2025-10-01',
    dueDate: '2025-10-15',
    description: 'Suite - 3 Nights',
    amount: 840,
    tax: 84,
    total: 924,
    status: 'Paid',
    paymentMethod: 'Debit Card',
    paymentDate: '2025-10-01',
    items: [
      { description: 'Suite Room (3 nights @ $280/night)', amount: 840 },
      { description: 'Tax (10%)', amount: 84 },
    ],
  },
  {
    id: 'INV-2024',
    bookingId: 'BK2024',
    date: '2025-08-25',
    dueDate: '2025-09-05',
    description: 'Standard Room - 3 Nights',
    amount: 360,
    tax: 36,
    total: 396,
    status: 'Paid',
    paymentMethod: 'Credit Card',
    paymentDate: '2025-08-25',
    items: [
      { description: 'Standard Room (3 nights @ $120/night)', amount: 360 },
      { description: 'Tax (10%)', amount: 36 },
    ],
  },
  {
    id: 'INV-2023',
    bookingId: 'SO2026',
    date: '2025-11-15',
    dueDate: '2025-11-15',
    description: 'Room Service - Breakfast',
    amount: 28,
    tax: 2.8,
    total: 30.8,
    status: 'Paid',
    paymentMethod: 'Room Charge',
    paymentDate: '2025-11-15',
    items: [
      { description: 'Continental Breakfast', amount: 28 },
      { description: 'Tax (10%)', amount: 2.8 },
    ],
  },
  {
    id: 'INV-2022',
    bookingId: 'SO2025',
    date: '2025-11-14',
    dueDate: '2025-11-14',
    description: 'Spa - Swedish Massage',
    amount: 80,
    tax: 8,
    total: 88,
    status: 'Paid',
    paymentMethod: 'Credit Card',
    paymentDate: '2025-11-14',
    items: [
      { description: 'Swedish Massage (60 min)', amount: 80 },
      { description: 'Tax (10%)', amount: 8 },
    ],
  },
];

export default function CustomerInvoices() {
  const [invoices] = useState(invoiceData);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const statusFilters = ['All', 'Paid', 'Pending', 'Overdue'];

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch = invoice.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          invoice.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 0 || invoice.status === statusFilters[activeTab];
    return matchesSearch && matchesTab;
  });

  const stats = [
    {
      label: 'Total Invoices',
      value: invoices.length,
      icon: Receipt,
      color: '#1976d2',
    },
    {
      label: 'Paid',
      value: invoices.filter(i => i.status === 'Paid').length,
      icon: CheckCircle,
      color: '#2e7d32',
    },
    {
      label: 'Total Paid',
      value: `$${invoices.filter(i => i.status === 'Paid').reduce((sum, i) => sum + i.total, 0).toFixed(2)}`,
      icon: AttachMoney,
      color: '#9c27b0',
    },
    {
      label: 'This Year',
      value: `$${invoices.filter(i => new Date(i.date).getFullYear() === 2025).reduce((sum, i) => sum + i.total, 0).toFixed(2)}`,
      icon: CreditCard,
      color: '#ed6c02',
    },
  ];

  const handleMenuClick = (event, invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleViewInvoice = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDownloadInvoice = () => {
    alert(`Downloading invoice ${selectedInvoice?.id}...`);
    setAnchorEl(null);
  };

  const handlePrintInvoice = () => {
    alert(`Printing invoice ${selectedInvoice?.id}...`);
    setAnchorEl(null);
  };

  const handleEmailInvoice = () => {
    alert(`Emailing invoice ${selectedInvoice?.id}...`);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Invoices & Payments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage your payment history and invoices
        </Typography>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            {statusFilters.map((filter) => (
              <Tab key={filter} label={filter} />
            ))}
          </Tabs>
        </Box>
        <Box p={2}>
          <TextField
            fullWidth
            placeholder="Search by invoice ID or description..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Invoice ID</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Tax</strong></TableCell>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Payment Method</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.map((invoice) => (
              <TableRow key={invoice.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Receipt color="primary" fontSize="small" />
                    <strong>{invoice.id}</strong>
                  </Box>
                </TableCell>
                <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Typography variant="body2">{invoice.description}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ref: {invoice.bookingId}
                  </Typography>
                </TableCell>
                <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                <TableCell>${invoice.tax.toFixed(2)}</TableCell>
                <TableCell><strong>${invoice.total.toFixed(2)}</strong></TableCell>
                <TableCell>
                  <Chip
                    label={invoice.status}
                    size="small"
                    color={
                      invoice.status === 'Paid' ? 'success' :
                      invoice.status === 'Pending' ? 'warning' : 'error'
                    }
                    icon={invoice.status === 'Paid' ? <CheckCircle /> : <Pending />}
                  />
                </TableCell>
                <TableCell>{invoice.paymentMethod}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, invoice)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleViewInvoice}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleDownloadInvoice}>
          <Download fontSize="small" sx={{ mr: 1 }} />
          Download PDF
        </MenuItem>
        <MenuItem onClick={handlePrintInvoice}>
          <Print fontSize="small" sx={{ mr: 1 }} />
          Print
        </MenuItem>
        <MenuItem onClick={handleEmailInvoice}>
          <Email fontSize="small" sx={{ mr: 1 }} />
          Email Invoice
        </MenuItem>
      </Menu>

      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Receipt color="primary" />
              <Typography variant="h6">Invoice Details</Typography>
            </Box>
            <Chip
              label={selectedInvoice?.status}
              color={selectedInvoice?.status === 'Paid' ? 'success' : 'warning'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">HotelOpsPro</Typography>
                      <Typography variant="body2" color="text.secondary">
                        123 Hotel Street, New York, NY 10001
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Phone: (555) 123-4567
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        {selectedInvoice.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Date: {new Date(selectedInvoice.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Bill To:</strong>
                  </Typography>
                  <Typography variant="body2">Emily Johnson</Typography>
                  <Typography variant="body2" color="text.secondary">
                    emily.johnson@gmail.com
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    +1 (555) 234-5678
                  </Typography>
                </Grid>

                <Grid item xs={6} textAlign="right">
                  <Typography variant="body2" color="text.secondary">
                    <strong>Booking Reference:</strong> {selectedInvoice.bookingId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Due Date:</strong> {new Date(selectedInvoice.dueDate).toLocaleDateString()}
                  </Typography>
                  {selectedInvoice.paymentDate && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Paid On:</strong> {new Date(selectedInvoice.paymentDate).toLocaleDateString()}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Items</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Description</strong></TableCell>
                          <TableCell align="right"><strong>Amount</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvoice.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell align="right">${item.amount.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Box width="300px">
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Subtotal:</Typography>
                        <Typography variant="body2">${selectedInvoice.amount.toFixed(2)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Tax (10%):</Typography>
                        <Typography variant="body2">${selectedInvoice.tax.toFixed(2)}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" fontWeight="bold">Total:</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          ${selectedInvoice.total.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mt={2} p={1} bgcolor="#f5f5f5" borderRadius={1}>
                        <Typography variant="body2"><strong>Payment Method:</strong></Typography>
                        <Typography variant="body2">{selectedInvoice.paymentMethod}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Print />} onClick={handlePrintInvoice}>
            Print
          </Button>
          <Button variant="contained" startIcon={<Download />} onClick={handleDownloadInvoice}>
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
